'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Check, ArrowRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/routing'
import { N_FREE_ITEMS, N_FREE_SECTIONS } from '@/config'
import '../../sass/landing/PricingV2.scss'

const PricingV2 = () => {
  const t = useTranslations('LandingV2.pricing')
  const [formattedPrice, setFormattedPrice] = useState('15€')
  const [currency, setCurrency] = useState('€')

  useEffect(() => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const isAmerican = timezone.toLowerCase().includes('america')
      if (isAmerican) {
        setCurrency('$')
        setFormattedPrice('$15')
      }
    } catch {
      /* noop */
    }
  }, [])

  return (
    <section id='pricing' className='pricing'>
      <div className='pricing__container'>
        <div className='pricing__header'>
          <span className='landing-v2__chip'>{t('chip')}</span>
          <h2>{t('heading')}</h2>
          <p>{t('description')}</p>
        </div>

        <div className='pricing__grid'>
          <motion.div
            className='pricing__card'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <div className='pricing__plan-name'>{t('free.name')}</div>
              <p className='pricing__plan-desc'>{t('free.description')}</p>
            </div>
            <div className='pricing__price'>
              <span className='pricing__amount'>{currency}0</span>
              <span className='pricing__period'>{t('free.period')}</span>
            </div>
            <div className='pricing__divider' />
            <ul className='pricing__list'>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('free.perks.p1', { N_FREE_ITEMS })}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('free.perks.p2', { N_FREE_SECTIONS })}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('free.perks.p3')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('free.perks.p4')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('free.perks.p5')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('free.perks.p6')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('free.perks.p7')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('free.perks.p8')}
              </li>
            </ul>
            <Link href='/login/signup' className='pricing__cta pricing__cta--outline'>
              {t('free.cta')}
            </Link>
          </motion.div>

          <motion.div
            className='pricing__card pricing__card--highlight'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <span className='pricing__badge'>{t('premium.badge')}</span>
            <div>
              <div className='pricing__plan-name'>{t('premium.name')}</div>
              <p className='pricing__plan-desc'>{t('premium.description')}</p>
            </div>
            <div className='pricing__price'>
              <span className='pricing__amount'>{formattedPrice}</span>
              <span className='pricing__period'>{t('premium.period')}</span>
            </div>
            <div className='pricing__divider' />
            <ul className='pricing__list'>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('premium.perks.p1')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('premium.perks.p2')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('premium.perks.p3')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('premium.perks.p4')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('premium.perks.p5')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('premium.perks.p6')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('premium.perks.p7')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('premium.perks.p8')}
              </li>
              <li className='pricing__list-item'>
                <Check size={16} /> {t('premium.perks.p9')}
              </li>
            </ul>
            <Link href='/login/signup' className='pricing__cta'>
              {t('premium.cta')} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>

        <p className='pricing__footnote'>
          {t('footnote.prefix')} <Link href='/pricing'>{t('footnote.link')}</Link>
        </p>
      </div>
    </section>
  )
}

export default PricingV2
